import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { listSequences, type Sequence } from '../api/sequences'
import { navigate } from '../App'
import { colors } from '../theme'

const SEQUENCES_GRID_COLUMNS = '1.6fr 0.6fr 0.6fr 0.6fr 0.6fr 0.8fr 0.4fr'

export default function Sequences() {
  const { authHeaders, apiBase, user } = useAuth()
  const [items, setItems] = useState<Sequence[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    let cancelled = false
    setItems(null)
    setError(null)
    listSequences(apiBase, authHeaders(), { mine: !showAll })
      .then((rows) => !cancelled && setItems(rows))
      .catch(
        (e: unknown) =>
          !cancelled && setError(e instanceof Error ? e.message : 'Failed to load'),
      )
    return () => {
      cancelled = true
    }
  }, [apiBase, authHeaders, showAll])

  return (
    <div>
      <SectionHeader
        eyebrow={showAll ? 'All Parkify Sequences' : `Owned by ${user?.email}`}
        title="Sequences"
        actions={
          <>
            <button
              onClick={() => setShowAll((v) => !v)}
              style={{
                background: colors.white,
                border: `1px solid ${colors.divider}`,
                color: colors.black,
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {showAll ? 'Show mine only' : 'Show all reps'}
            </button>
            <button
              onClick={() => navigate({ name: 'prospect' })}
              style={{
                background: colors.white,
                border: `1px solid ${colors.purple}`,
                color: colors.purple,
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Find prospects
            </button>
            <button
              onClick={() => navigate({ name: 'sequence-new' })}
              style={{
                background: colors.purple,
                color: colors.white,
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              + New sequence
            </button>
          </>
        }
      />

      {error ? (
        <ErrorBlock message={error} />
      ) : items === null ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <Table rows={items} />
      )}
    </div>
  )
}

function SectionHeader({
  eyebrow,
  title,
  actions,
}: {
  eyebrow: string
  title: string
  actions?: React.ReactNode
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '2.5px',
            color: colors.purple,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            fontSize: 32,
            fontWeight: 700,
            letterSpacing: '-1px',
            margin: 0,
            color: colors.black,
          }}
        >
          {title}
        </h1>
      </div>
      {actions ? <div style={{ display: 'flex', gap: 8 }}>{actions}</div> : null}
    </div>
  )
}

function Table({ rows }: { rows: Sequence[] }) {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        overflowX: 'auto',
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: SEQUENCES_GRID_COLUMNS,
          minWidth: 760,
          padding: '14px 20px',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: colors.purple,
          borderBottom: `1px solid ${colors.divider}`,
          background: colors.bgAlt,
        }}
      >
        <div>Name</div>
        <div>Steps</div>
        <div>Enrolled</div>
        <div>Sent</div>
        <div>Replied</div>
        <div>Status</div>
        <div></div>
      </div>
      {rows.map((r, idx) => (
        <button
          key={r.id}
          onClick={() => navigate({ name: 'sequence-detail', id: r.id })}
          style={{
            display: 'grid',
            gridTemplateColumns: SEQUENCES_GRID_COLUMNS,
            minWidth: 760,
            width: '100%',
            padding: '16px 20px',
            background: colors.white,
            border: 'none',
            borderBottom:
              idx === rows.length - 1 ? 'none' : `1px solid ${colors.divider}`,
            textAlign: 'left',
            color: colors.black,
            cursor: 'pointer',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLElement).style.background = colors.purpleSoft
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLElement).style.background = colors.white
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: colors.black }}>{r.name}</div>
            <div
              style={{
                fontSize: 12,
                color: colors.textMuted,
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 360,
              }}
            >
              {r.target_audience || r.description || 'No audience description'}
            </div>
          </div>
          <div style={{ color: colors.textMuted, fontSize: 14 }}>
            {r.steps?.length ?? 0}
          </div>
          <div style={{ color: colors.textMuted, fontSize: 14 }}>
            {r.stats?.enrolled_count ?? 0}
          </div>
          <div style={{ color: colors.textMuted, fontSize: 14 }}>
            {r.stats?.sent_count ?? 0}
          </div>
          <div style={{ color: colors.textMuted, fontSize: 14 }}>
            {r.stats?.replied_count ?? 0}
          </div>
          <div>
            <StatusBadge status={r.status} />
          </div>
          <div
            style={{
              textAlign: 'right',
              color: colors.purple,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            ›
          </div>
        </button>
      ))}
    </div>
  )
}

function StatusBadge({ status }: { status: Sequence['status'] }) {
  const tone =
    status === 'active'
      ? { bg: colors.purpleSoft, fg: colors.purple }
      : status === 'paused'
        ? { bg: colors.bgAlt, fg: colors.textMuted }
        : { bg: colors.warningBg, fg: colors.red }
  return (
    <span
      style={{
        background: tone.bg,
        color: tone.fg,
        borderRadius: 999,
        padding: '4px 10px',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
      }}
    >
      {status}
    </span>
  )
}

function SkeletonRows() {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: 60,
            borderBottom: i === 3 ? 'none' : `1px solid ${colors.divider}`,
            background: `linear-gradient(90deg, transparent, rgba(127,63,152,0.06), transparent)`,
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s linear infinite',
          }}
        />
      ))}
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  )
}

function EmptyState() {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px dashed ${colors.gray}`,
        borderRadius: 12,
        padding: '60px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '3px',
          color: colors.purple,
          marginBottom: 12,
          textTransform: 'uppercase',
        }}
      >
        No Sequences Yet
      </div>
      <h2
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          margin: '0 0 8px',
          color: colors.black,
        }}
      >
        Describe who you want to reach. The portal drafts the touches.
      </h2>
      <p
        style={{
          fontSize: 14,
          color: colors.textMuted,
          maxWidth: 520,
          margin: '0 auto 20px',
          lineHeight: 1.6,
        }}
      >
        A sequence is a multi-step outbound cadence. You write the audience
        and goal in plain English; the LLM drafts subjects, body prompts,
        and timing. Per-contact copy is generated at send time.
      </p>
      <div style={{ display: 'inline-flex', gap: 10 }}>
        <button
          onClick={() => navigate({ name: 'sequence-new' })}
          style={{
            background: colors.purple,
            color: colors.white,
            border: 'none',
            borderRadius: 10,
            padding: '12px 22px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          + Draft a sequence
        </button>
        <button
          onClick={() => navigate({ name: 'prospect' })}
          style={{
            background: colors.white,
            color: colors.purple,
            border: `1px solid ${colors.purple}`,
            borderRadius: 10,
            padding: '12px 22px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Find prospects
        </button>
      </div>
    </div>
  )
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '14px 18px',
        background: colors.warningBg,
        border: `1.5px solid ${colors.warningBorder}`,
        borderRadius: 10,
        fontSize: 13,
        color: colors.black,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ color: colors.red, fontWeight: 700 }}>⚠</span>
      Couldn't load sequences: {message}
    </div>
  )
}
